import React, { useState, useCallback, memo, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowUpDown, Twitter, TrendingUp, TrendingDown, CircleDollarSign, BarChart3, ChevronLeft, ChevronRight } from 'lucide-react';
import type { SubnetMindshare } from '../types/mindshare';
import { FixedSizeList as List } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';

interface MindshareTableProps {
  mindshareData: SubnetMindshare[];
  loading: boolean;
  percentChanges?: Record<number, number>;
}

type SortField = 'mindsharePercentage' | 'twitterEngagement' | 'marketCapScore' | 'rankScore' | 'volumeScore' | 'percentChange';

// Memoized row component for better performance
const TableRow = memo(({ data, index, style }: { 
  data: {
    items: SubnetMindshare[];
    percentChanges: Record<number, number>;
    startIndex: number;
    onSubnetClick: (netuid: number) => void;
  }, 
  index: number, 
  style: React.CSSProperties 
}) => {
  const { items, percentChanges, startIndex, onSubnetClick } = data;
  const item = items[index];
  if (!item) return null;
  
  const actualIndex = startIndex + index;

  return (
    <div 
      style={style}
      className="border-b border-white/5 hover:bg-black/20 cursor-pointer transition-colors"
      onClick={() => onSubnetClick(item.netuid)}
    >
      <div className="grid grid-cols-7 h-full">
        <div className="p-3 text-sm flex items-center">{actualIndex + 1}</div>
        <div className="p-3 text-sm font-medium flex items-center">{item.name}</div>
        <div className="p-3 text-sm font-bold text-cyan-400 flex items-center">{item.mindsharePercentage.toFixed(1)}%</div>
        <div className="p-3 flex items-center">
          <div className="w-full bg-black/30 rounded-full h-2">
            <div 
              className="bg-blue-500 h-2 rounded-full" 
              style={{ width: `${item.twitterEngagement}%` }}
            ></div>
          </div>
        </div>
        <div className="p-3 flex items-center">
          <div className="w-full bg-black/30 rounded-full h-2">
            <div 
              className="bg-green-500 h-2 rounded-full" 
              style={{ width: `${item.marketCapScore}%` }}
            ></div>
          </div>
        </div>
        <div className="p-3 flex items-center">
          <div className="w-full bg-black/30 rounded-full h-2">
            <div 
              className="bg-purple-500 h-2 rounded-full" 
              style={{ width: `${item.rankScore}%` }}
            ></div>
          </div>
        </div>
        <div className="p-3 flex items-center">
          <div className="w-full bg-black/30 rounded-full h-2">
            <div 
              className="bg-orange-500 h-2 rounded-full" 
              style={{ width: `${item.volumeScore}%` }}
            ></div>
          </div>
        </div>
      </div>
    </div>
  );
});

// Table header component
const TableHeader = memo(({ onSort, sortField, sortDirection }: {
  onSort: (field: SortField) => void;
  sortField: SortField;
  sortDirection: 'asc' | 'desc';
}) => {
  const renderSortIcon = (field: SortField) => {
    if (sortField !== field) return <ArrowUpDown className="w-4 h-4 text-gray-500" />;
    return sortDirection === 'asc' 
      ? <TrendingUp className="w-4 h-4 text-cyan-400" />
      : <TrendingDown className="w-4 h-4 text-cyan-400" />;
  };

  return (
    <div className="bg-black/30 text-left sticky top-0 z-10">
      <div className="grid grid-cols-7">
        <div className="p-3 text-sm font-semibold text-gray-300">Rank</div>
        <div className="p-3 text-sm font-semibold text-gray-300">Subnet</div>
        <div 
          className="p-3 text-sm font-semibold text-gray-300 cursor-pointer"
          onClick={() => onSort('mindsharePercentage')}
        >
          <div className="flex items-center space-x-1">
            <span>Mindshare</span>
            {renderSortIcon('mindsharePercentage')}
          </div>
        </div>
        <div 
          className="p-3 text-sm font-semibold text-gray-300 cursor-pointer"
          onClick={() => onSort('twitterEngagement')}
        >
          <div className="flex items-center space-x-1">
            <Twitter className="w-4 h-4" />
            <span>Twitter</span>
            {renderSortIcon('twitterEngagement')}
          </div>
        </div>
        <div 
          className="p-3 text-sm font-semibold text-gray-300 cursor-pointer"
          onClick={() => onSort('marketCapScore')}
        >
          <div className="flex items-center space-x-1">
            <CircleDollarSign className="w-4 h-4" />
            <span>Market Cap</span>
            {renderSortIcon('marketCapScore')}
          </div>
        </div>
        <div 
          className="p-3 text-sm font-semibold text-gray-300 cursor-pointer"
          onClick={() => onSort('rankScore')}
        >
          <div className="flex items-center space-x-1">
            <TrendingUp className="w-4 h-4" />
            <span>Rank</span>
            {renderSortIcon('rankScore')}
          </div>
        </div>
        <div 
          className="p-3 text-sm font-semibold text-gray-300 cursor-pointer"
          onClick={() => onSort('volumeScore')}
        >
          <div className="flex items-center space-x-1">
            <BarChart3 className="w-4 h-4" />
            <span>Volume</span>
            {renderSortIcon('volumeScore')}
          </div>
        </div>
      </div>
    </div>
  );
});

export function MindshareTable({ mindshareData, loading, percentChanges = {} }: MindshareTableProps) {
  const navigate = useNavigate();
  const [sortField, setSortField] = useState<SortField>('mindsharePercentage');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const handleSort = useCallback((field: SortField) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
    setCurrentPage(1); // Reset to first page when sorting changes
  }, [sortField, sortDirection]);

  // Memoize sorted data to prevent unnecessary recalculations
  const sortedData = useMemo(() => {
    return [...mindshareData].sort((a, b) => {
      if (sortField === 'percentChange') {
        const changeA = percentChanges[a.netuid] || 0;
        const changeB = percentChanges[b.netuid] || 0;
        return sortDirection === 'asc' ? changeA - changeB : changeB - changeA;
      }
      
      const valueA = a[sortField];
      const valueB = b[sortField];
      
      if (sortDirection === 'asc') {
        return valueA - valueB;
      } else {
        return valueB - valueA;
      }
    });
  }, [mindshareData, sortField, sortDirection, percentChanges]);

  // Calculate pagination
  const totalPages = Math.ceil(sortedData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = useMemo(() => {
    return sortedData.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedData, startIndex, itemsPerPage]);

  const goToPage = useCallback((page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  }, [totalPages]);

  const handleSubnetClick = useCallback((netuid: number) => {
    // Navigate to the subnet page
    navigate(`/subnet/${netuid}`);
    
    // Reload the page to ensure fresh data
    setTimeout(() => {
      window.location.reload();
    }, 100);
  }, [navigate]);

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-10 bg-black/20 rounded-lg w-full"></div>
        <div className="h-64 bg-black/20 rounded-lg w-full"></div>
      </div>
    );
  }

  if (mindshareData.length === 0) {
    return (
      <div className="bg-black/20 backdrop-blur-sm rounded-lg p-4 text-center border border-white/10">
        <p className="text-gray-400">No mindshare data available</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto">
        <div className="w-full" style={{ height: '500px' }}>
          <TableHeader 
            onSort={handleSort} 
            sortField={sortField} 
            sortDirection={sortDirection} 
          />
          <AutoSizer>
            {({ height, width }) => (
              <List
                height={height - 40} // Subtract header height
                width={width}
                itemCount={paginatedData.length}
                itemSize={50} // Row height
                itemData={{
                  items: paginatedData,
                  percentChanges,
                  startIndex,
                  onSubnetClick: handleSubnetClick
                }}
              >
                {TableRow}
              </List>
            )}
          </AutoSizer>
        </div>
      </div>

      {/* Pagination Controls */}
      <div className="flex items-center justify-between mt-4">
        <div className="text-sm text-gray-400">
          Showing {startIndex + 1}-{Math.min(startIndex + itemsPerPage, sortedData.length)} of {sortedData.length} subnets
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 1}
            className={`p-2 rounded-lg ${
              currentPage === 1 
                ? 'text-gray-500 cursor-not-allowed' 
                : 'text-white hover:bg-black/20'
            }`}
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          
          <div className="flex items-center space-x-1">
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              // Show pages around current page
              let pageToShow = i + 1;
              if (totalPages > 5) {
                if (currentPage <= 3) {
                  pageToShow = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageToShow = totalPages - 4 + i;
                } else {
                  pageToShow = currentPage - 2 + i;
                }
              }
              
              return (
                <button
                  key={pageToShow}
                  onClick={() => goToPage(pageToShow)}
                  className={`px-3 py-1 rounded-lg text-sm ${
                    currentPage === pageToShow
                      ? 'bg-cyan-600 text-white'
                      : 'text-gray-400 hover:bg-black/20'
                  }`}
                >
                  {pageToShow}
                </button>
              );
            })}
          </div>

          <button
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`p-2 rounded-lg ${
              currentPage === totalPages 
                ? 'text-gray-500 cursor-not-allowed' 
                : 'text-white hover:bg-black/20'
            }`}
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}